/**
 * An asset's versions (issues #199, #217): each is a source — where the art
 * came from, e.g. d3 or d3-generated — plus a version number, with the input it
 * was made from (an image, or a PSD it was flattened or extracted from) and the
 * outputs it serves (its image, or a tile brush's pieces). Staff delete a
 * version or a whole source, choose each source's current version, and pin
 * what each app serves.
 */

/**
 * @typedef {object} VersionFileRef
 * @property {string} id
 * @property {string} [file_name]
 * @property {string} [mime_type]
 * @property {number} [file_size]
 * @property {number | null} [width]
 * @property {number | null} [height]
 * @property {string} url
 */

/**
 * @typedef {object} VersionFile
 * @property {string} role
 * @property {VersionFileRef} file
 */

/**
 * @typedef {object} AssetVersion
 * @property {string} id
 * @property {string} source
 * @property {number} version
 * @property {boolean} is_current
 * @property {string} [created_at]
 * @property {import('./authors.js').Author | null} [author] who made it, when known
 * @property {VersionFile[]} inputs
 * @property {VersionFile[]} outputs
 */

/**
 * @typedef {object} VersionGroup
 * @property {string} source
 * @property {AssetVersion[]} versions newest first
 */

export const PSD_MIME_TYPE = 'image/vnd.adobe.photoshop';

/**
 * An asset's versions grouped by source, alphabetical, newest first.
 * @param {AssetVersion[]} versions
 * @returns {VersionGroup[]}
 */
export function versionGroups(versions) {
    /** @type {Record<string, VersionGroup>} */
    const groups = {};
    for (const v of versions || []) {
        groups[v.source] ??= { source: v.source, versions: [] };
        groups[v.source].versions.push(v);
    }
    for (const g of Object.values(groups)) g.versions.sort((a, b) => b.version - a.version);
    return Object.values(groups).sort((a, b) => a.source.localeCompare(b.source));
}

/**
 * The output that stands for a version where one file is shown: its image, or
 * for a tile brush its first piece by role.
 * @param {AssetVersion} version
 * @returns {VersionFile | undefined}
 */
export function previewOutput(version) {
    const outputs = version.outputs || [];
    return (
        outputs.find((o) => o.role === 'image') ??
        [...outputs].sort((a, b) => a.role.localeCompare(b.role))[0]
    );
}

/**
 * The version's input when it's a separate file from what it serves — a PSD it
 * was flattened or extracted from — or undefined when the input is the image.
 * @param {AssetVersion} version
 * @returns {VersionFile | undefined}
 */
export function separateInput(version) {
    const input = (version.inputs || []).find((i) => i.role === 'source');
    if (!input) return undefined;
    return (version.outputs || []).some(
        (o) => o.file.id === input.file.id || o.file.url === input.file.url,
    )
        ? undefined
        : input;
}

/**
 * The newest current version: the one the asset shows.
 * @param {AssetVersion[]} versions
 * @returns {AssetVersion | undefined}
 */
export function currentVersion(versions) {
    return (versions || [])
        .filter((v) => v.is_current)
        .sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')))[0];
}

/**
 * The API path that deletes a whole source, or one version of it.
 * @param {string} assetId
 * @param {string} source
 * @param {number} [version]
 * @returns {string}
 */
export function deleteVersionsPath(assetId, source, version) {
    const path = `/api/assets/${assetId}/versions/${encodeURIComponent(source)}`;
    return version == null ? path : `${path}/${version}`;
}

/**
 * What deleting a whole source deletes, to confirm.
 * @param {VersionGroup[]} groups
 * @param {string} source
 * @returns {string}
 */
export function deleteSourceSummary(groups, source) {
    const count = groups.find((g) => g.source === source)?.versions.length ?? 0;
    return `Delete all ${count} version${count === 1 ? '' : 's'} of ${source}?`;
}

/**
 * How an app's pin reads: the default, a source's latest, or one version.
 * @param {{ source: string, version: number | null } | null} pin
 * @returns {string}
 */
export function pinLabel(pin) {
    if (!pin) return 'Default (d3 latest)';
    return pin.version == null ? `${pin.source} latest` : `${pin.source} v${pin.version}`;
}

/**
 * @typedef {object} PreviewRow
 * @property {string} id the version's id
 * @property {string} source
 * @property {number} version
 * @property {boolean} is_current
 * @property {string} image_url the version's preview output
 * @property {string} [file_name]
 * @property {number | null} [width]
 * @property {number | null} [height]
 */

/**
 * Each version as the one file it shows (its preview output), for the
 * per-source image selectors.
 * @param {AssetVersion[]} versions
 * @returns {PreviewRow[]}
 */
export function previewRows(versions) {
    return (versions || []).flatMap((v) => {
        const out = previewOutput(v);
        if (!out) return [];
        return [
            {
                id: v.id,
                source: v.source,
                version: v.version,
                is_current: v.is_current,
                image_url: out.file.url,
                file_name: out.file.file_name,
                width: out.file.width,
                height: out.file.height,
            },
        ];
    });
}
